"use client";

/**
 * Wizard stepper visível — #483 D3 (design sancionado, grill Lucas + S0,
 * 2026-07-05): "etapas travadas na ordem (Upload -> Cut-lines -> Calcular ->
 * Revisar -> Baixar; previne erro de operador) com a trilha inteira sempre
 * visível (operador vê o que vem na frente)".
 *
 * 5 passos lógicos mapeiam a 3 rotas físicas (Upload = hub `/[lev_id]`;
 * Cut-lines + Calcular = `/[lev_id]/scene`; Revisar + Baixar =
 * `/[lev_id]/results`) — não existem 5 telas novas, o stepper é um indicador
 * de progresso + atalho de navegação sobre as páginas já existentes.
 *
 * `onNavigate` é injetado pelo caller (cada página já tem seu próprio
 * `useRouter()`) em vez deste componente importar `next/navigation`
 * diretamente — mantém o componente testável via render real sem precisar
 * mockar o roteador em todo test harness que o monta.
 */

import { CheckIcon } from "lucide-react";

export type WizardStepKey =
  | "upload"
  | "cut-lines"
  | "calcular"
  | "revisar"
  | "baixar";

interface StepDef {
  key: WizardStepKey;
  label: string;
  href: (levId: string) => string;
}

const STEPS: StepDef[] = [
  { key: "upload", label: "Upload", href: (id) => `/${id}` },
  { key: "cut-lines", label: "Cut-lines", href: (id) => `/${id}/scene` },
  { key: "calcular", label: "Calcular", href: (id) => `/${id}/scene` },
  { key: "revisar", label: "Revisar", href: (id) => `/${id}/results` },
  { key: "baixar", label: "Baixar", href: (id) => `/${id}/results` },
];

export interface WizardStepperProps {
  levId: string;
  /** Etapa em destaque no momento (não necessariamente a última desbloqueada
   * — ex.: scene page pode destacar "calcular" enquanto a mutation roda). */
  current: WizardStepKey;
  /** Vídeo(s) já processado(s) (status='processado'|'validated') — libera Cut-lines. */
  videoReady: boolean;
  /** Ao menos 1 cut-line desenhada — libera Calcular. */
  hasCutLines: boolean;
  /** Ao menos 1 count_run existente — libera Revisar + Baixar. */
  hasRuns: boolean;
  onNavigate: (href: string) => void;
}

export function WizardStepper({
  levId,
  current,
  videoReady,
  hasCutLines,
  hasRuns,
  onNavigate,
}: WizardStepperProps) {
  // maxUnlockedIndex — índice do passo mais avançado já alcançável. Ordem
  // travada (#483 D3): cada passo só libera o próximo depois de satisfeita
  // a pré-condição do anterior.
  let maxUnlockedIndex = 0; // Upload sempre navegável (passo 1, ponto de partida)
  if (videoReady) maxUnlockedIndex = 1; // Cut-lines
  if (videoReady && hasCutLines) maxUnlockedIndex = 2; // Calcular
  if (videoReady && hasRuns) maxUnlockedIndex = 4; // Revisar + Baixar (mesma rota)

  const currentIndex = STEPS.findIndex((s) => s.key === current);

  return (
    <nav
      aria-label="Progresso do levantamento"
      data-testid="wizard-stepper"
      className="flex flex-wrap items-center gap-1 border-b border-tide-border bg-tide-bg-secondary px-4 py-2 text-xs"
    >
      {STEPS.map((step, idx) => {
        const isLocked = idx > maxUnlockedIndex;
        const isCurrent = idx === currentIndex;
        const isDone = idx < currentIndex && !isLocked;
        return (
          <div key={step.key} className="flex items-center gap-1">
            {idx > 0 && (
              <span className="text-tide-text-muted" aria-hidden="true">
                →
              </span>
            )}
            <button
              type="button"
              disabled={isLocked}
              data-testid={`wizard-step-${step.key}`}
              aria-current={isCurrent ? "step" : undefined}
              title={
                isLocked
                  ? "Conclua a etapa anterior para desbloquear"
                  : undefined
              }
              onClick={() => onNavigate(step.href(levId))}
              className={[
                "rounded px-2 py-1 font-medium transition",
                isCurrent
                  ? "bg-tide-accent text-white"
                  : isLocked
                    ? "cursor-not-allowed text-tide-text-muted/50"
                    : "text-tide-text-secondary hover:bg-tide-bg-hover",
              ].join(" ")}
            >
              {isDone && (
                <CheckIcon className="mr-1 inline size-3 align-[-1px]" />
              )}
              {step.label}
            </button>
          </div>
        );
      })}
    </nav>
  );
}
